import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { History } from "lucide-react";

type Entry = {
  id: string;
  audit_id: string;
  clause_ref: string | null;
  clause_title: string | null;
  framework: string | null;
  action: string;
  field: string | null;
  old_value: string | null;
  new_value: string | null;
  changed_by_name: string | null;
  created_at: string;
};

const FIELD_LABELS: Record<string, string> = {
  result: "Result",
  evidence: "Evidence",
  finding_notes: "Findings",
  corrective_action: "Corrective action",
  requirement: "Requirement",
};

const truncate = (s: string | null, n = 120) =>
  !s ? "—" : s.length > n ? s.slice(0, n) + "…" : s;

const AuditChangeLog = ({ auditId }: { auditId: string }) => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data } = await supabase
        .from("internal_audit_item_log" as any)
        .select("*")
        .eq("audit_id", auditId)
        .order("created_at", { ascending: false })
        .limit(200);
      if (active) {
        setEntries((data as any) || []);
        setLoading(false);
      }
    };
    load();
    const ch = supabase
      .channel(`audit-log-${auditId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "internal_audit_item_log", filter: `audit_id=eq.${auditId}` },
        (payload) => setEntries((prev) => [payload.new as Entry, ...prev]),
      )
      .subscribe();
    return () => {
      active = false;
      supabase.removeChannel(ch);
    };
  }, [auditId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <History className="w-4 h-4" /> Change history
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-primary-foreground/50">Loading...</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-primary-foreground/50">No changes recorded yet.</p>
        ) : (
          <ol className="space-y-3">
            {entries.map((e) => (
              <li key={e.id} className="rounded-lg border border-primary-foreground/10 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2 text-xs text-primary-foreground/60">
                  <Badge variant="outline" className="text-[10px] uppercase">{e.action}</Badge>
                  {e.framework && <Badge variant="outline" className="text-[10px]">{e.framework}</Badge>}
                  {e.clause_ref && <span className="font-mono">{e.clause_ref}</span>}
                  <span className="ml-auto">
                    {new Date(e.created_at).toLocaleString("en-US")} ·{" "}
                    <span className="text-primary-foreground/80">{e.changed_by_name || "System"}</span>
                  </span>
                </div>
                {e.clause_title && <p className="mt-1 text-primary-foreground/80">{e.clause_title}</p>}
                {e.field && (
                  <div className="mt-2 text-xs">
                    <span className="font-medium text-primary-foreground/70">{FIELD_LABELS[e.field] || e.field}: </span>
                    <span className="line-through text-red-300/70">{truncate(e.old_value)}</span>
                    <span className="mx-1 text-primary-foreground/40">→</span>
                    <span className="text-emerald-300/90">{truncate(e.new_value)}</span>
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};

export default AuditChangeLog;
